import React from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Button } from './ui/button';
import {
  Brain,
  Eye,
  TrendingUp,
  BarChart3,
  Zap,
  Shield,
  ScanSearch,
  CheckCircle,
  ArrowRight
} from 'lucide-react';

const mlUseCases = [
  {
    title: "Prediccion de Ventas",
    description: "Predice ventas futuras con 95% de precision usando modelos entrenados con tus datos.",
    metric: "+40% ROI",
    icon: TrendingUp
  },
  {
    title: "Analisis de Sentimientos",
    description: "Analiza opiniones de clientes automaticamente para tomar decisiones informadas.",
    metric: "92% Precision",
    icon: BarChart3
  },
  {
    title: "Deteccion de Fraudes",
    description: "Identifica transacciones sospechosas al instante con modelos predictivos.",
    metric: "99.8% Deteccion",
    icon: Zap
  }
];

const cvUseCases = [
  {
    title: "Control de Calidad",
    description: "Detecta defectos en productos automaticamente antes de que lleguen al cliente.",
    metric: "99% Precision",
    icon: CheckCircle,
    example: "Inspeccion de piezas en linea de produccion"
  },
  {
    title: "Seguridad Inteligente",
    description: "Identifica personas, vehiculos y situaciones de riesgo en tiempo real.",
    metric: "24/7 Monitoreo",
    icon: Shield,
    example: "Alertas automaticas de acceso no autorizado"
  },
  {
    title: "Lectura de Documentos",
    description: "Extrae datos de facturas, formularios y documentos escaneados al instante.",
    metric: "95% Ahorro Tiempo",
    icon: ScanSearch,
    example: "Procesamiento automatico de facturas"
  }
];

export const AICapabilitiesSection = () => {
  const scrollToContact = () => {
    document.getElementById('contacto')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section className="py-24 lg:py-32 bg-secondary/50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <span className="text-sm font-medium text-accent uppercase tracking-[0.2em] mb-4 block">
            Capacidades
          </span>
          <h2 className="font-display text-4xl lg:text-5xl font-bold text-foreground leading-tight mb-4 text-balance">
            Inteligencia Artificial <span className="italic">de verdad</span>
          </h2>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto leading-relaxed">
            Tecnologia de vanguardia para automatizar y potenciar tu negocio
          </p>
        </div>

        <Tabs defaultValue="ml" className="w-full">
          <TabsList className="flex w-full max-w-sm mx-auto mb-12 bg-foreground/5 rounded-full p-1">
            <TabsTrigger
              value="ml"
              className="flex-1 rounded-full data-[state=active]:bg-foreground data-[state=active]:text-background text-sm font-medium transition-all duration-300 py-2.5 flex items-center justify-center gap-2"
            >
              <Brain className="h-4 w-4" />
              Machine Learning
            </TabsTrigger>
            <TabsTrigger
              value="cv"
              className="flex-1 rounded-full data-[state=active]:bg-foreground data-[state=active]:text-background text-sm font-medium transition-all duration-300 py-2.5 flex items-center justify-center gap-2"
            >
              <Eye className="h-4 w-4" />
              Vision Computacional
            </TabsTrigger>
          </TabsList>

          {/* Machine Learning */}
          <TabsContent value="ml">
            <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
              {mlUseCases.map((useCase, index) => {
                const IconComponent = useCase.icon;
                return (
                  <div
                    key={index}
                    className="group bg-card border border-border rounded-2xl p-8 transition-all duration-500 hover:border-foreground/20 hover:shadow-lg hover:-translate-y-1"
                  >
                    <div className="w-12 h-12 rounded-xl bg-foreground/5 flex items-center justify-center mb-6 group-hover:bg-accent/10 transition-colors duration-300">
                      <IconComponent className="h-6 w-6 text-foreground group-hover:text-accent transition-colors duration-300" />
                    </div>
                    <h4 className="text-lg font-bold text-foreground mb-2">{useCase.title}</h4>
                    <p className="text-muted-foreground text-sm leading-relaxed mb-4">{useCase.description}</p>
                    <span className="inline-block bg-accent/10 text-accent px-4 py-1.5 rounded-full text-sm font-semibold">
                      {useCase.metric}
                    </span>
                  </div>
                );
              })}
            </div>
          </TabsContent>

          {/* Computer Vision */}
          <TabsContent value="cv">
            <div className="text-center max-w-2xl mx-auto mb-8">
              <p className="text-muted-foreground leading-relaxed">
                <span className="font-semibold text-foreground">Vision Computacional</span> permite que las computadoras
                "vean" y entiendan imagenes y videos, automatizando tareas visuales.
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
              {cvUseCases.map((useCase, index) => {
                const IconComponent = useCase.icon;
                return (
                  <div
                    key={index}
                    className="group bg-card border border-border rounded-2xl p-8 transition-all duration-500 hover:border-foreground/20 hover:shadow-lg hover:-translate-y-1"
                  >
                    <div className="w-12 h-12 rounded-xl bg-foreground/5 flex items-center justify-center mb-6 group-hover:bg-accent/10 transition-colors duration-300">
                      <IconComponent className="h-6 w-6 text-foreground group-hover:text-accent transition-colors duration-300" />
                    </div>
                    <h4 className="text-lg font-bold text-foreground mb-2">{useCase.title}</h4>
                    <p className="text-muted-foreground text-sm leading-relaxed mb-3">{useCase.description}</p>
                    <span className="inline-block bg-accent/10 text-accent px-4 py-1.5 rounded-full text-sm font-semibold mb-3">
                      {useCase.metric}
                    </span>
                    <p className="text-xs text-muted-foreground italic">{useCase.example}</p>
                  </div>
                );
              })}
            </div>
          </TabsContent>
        </Tabs>

        {/* CTA */}
        <div className="text-center mt-16">
          <div className="bg-card rounded-2xl border border-border p-10 lg:p-12 max-w-2xl mx-auto">
            <h3 className="font-display text-2xl lg:text-3xl font-bold text-foreground mb-4 text-balance">
              Listo para implementar IA en tu empresa?
            </h3>
            <p className="text-muted-foreground mb-8 leading-relaxed">
              Agenda una consulta gratuita y descubre como la IA puede transformar tu negocio.
            </p>
            <Button
              onClick={scrollToContact}
              className="bg-foreground text-background hover:bg-foreground/90 rounded-full px-8 py-3 text-base font-medium transition-all duration-300 group"
            >
              Implementar IA en mi Empresa
              <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform duration-300" />
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};
